import { ComponentsManager, Component } from '../core/smpl-components.js'

const IDLE_DELAY = 2500

/**
 * Native <video controls> that fade out of the way.
 *
 * The control bar stays on while the video is paused and while the viewer is
 * active; it is removed after IDLE_DELAY of no pointer/keyboard activity during
 * playback, and comes straight back on the next move, tap or focus.
 */
class BunnyPlayer {
	constructor(element) {
		this.element = element
		this.elVideo = this.element.querySelector('.js-external-video')

		if (!this.elVideo) {
			return
		}

		this.idleTimer = null

		this.bndActivity = this.onActivity.bind(this)
		this.bndPlay = this.onPlay.bind(this)
		this.bndStop = this.onStop.bind(this)

		this.element.addEventListener('mousemove', this.bndActivity)
		this.element.addEventListener('pointerdown', this.bndActivity)
		this.element.addEventListener('touchstart', this.bndActivity, { passive: true })
		this.element.addEventListener('focusin', this.bndActivity)

		this.elVideo.addEventListener('play', this.bndPlay)
		this.elVideo.addEventListener('playing', this.bndPlay)
		this.elVideo.addEventListener('pause', this.bndStop)
		this.elVideo.addEventListener('ended', this.bndStop)

		this.showControls()
	}

	clearTimer() {
		if (this.idleTimer) {
			clearTimeout(this.idleTimer)
			this.idleTimer = null
		}
	}

	// Only arm the timer while something is actually playing — a paused video
	// keeps its controls indefinitely.
	startTimer() {
		this.clearTimer()

		if (this.elVideo.paused || this.elVideo.ended) {
			return
		}

		this.idleTimer = setTimeout(() => {
			this.idleTimer = null
			this.hideControls()
		}, IDLE_DELAY)
	}

	showControls() {
		this.elVideo.controls = true
		this.startTimer()
	}

	hideControls() {
		if (this.elVideo.paused || this.elVideo.ended) {
			return
		}

		this.elVideo.controls = false
	}

	onActivity() {
		this.showControls()
	}

	onPlay() {
		this.startTimer()
	}

	onStop() {
		this.clearTimer()
		this.elVideo.controls = true
	}

	// Called straight out of the overlay's click handler, so the user gesture is
	// still live and playback may start unmuted. If the browser refuses anyway,
	// retry muted rather than leaving the viewer with a dead frame.
	play() {
		if (!this.elVideo) {
			return
		}

		// The overlay starts hidden, so ExternalSources sized off a clientWidth of 0
		// and loaded the smallest file. Re-pick now that the element has real
		// layout — doing it here rather than letting the debounced resize handler
		// fire means the resulting load() happens before playback, not 150ms into
		// it, which would abort it.
		const sources = ComponentsManager.getComponentByElement(this.elVideo, 'video-external')
		if (sources) {
			sources.updateSource()
		}

		this.showControls()

		const playPromise = this.elVideo.play()
		if (playPromise && typeof playPromise.catch === 'function') {
			playPromise.catch(() => {
				this.elVideo.muted = true
				this.elVideo.play().catch(() => {})
			})
		}
	}

	reset() {
		if (!this.elVideo) {
			return
		}

		this.clearTimer()
		this.elVideo.pause()
		this.elVideo.currentTime = 0
		this.elVideo.muted = false
		this.elVideo.controls = true
	}

	destroy() {
		if (!this.elVideo) {
			return
		}

		this.clearTimer()

		this.element.removeEventListener('mousemove', this.bndActivity)
		this.element.removeEventListener('pointerdown', this.bndActivity)
		this.element.removeEventListener('touchstart', this.bndActivity)
		this.element.removeEventListener('focusin', this.bndActivity)

		this.elVideo.removeEventListener('play', this.bndPlay)
		this.elVideo.removeEventListener('playing', this.bndPlay)
		this.elVideo.removeEventListener('pause', this.bndStop)
		this.elVideo.removeEventListener('ended', this.bndStop)
	}
}

// -------------------------------------------------------------------------- //
export default class extends Component {
	prepare() {
		this.bunnyPlayer = new BunnyPlayer(this.element)
	}

	mount() {}

	unmount() {
		if (this.bunnyPlayer) {
			this.bunnyPlayer.destroy()
			this.bunnyPlayer = null
		}
	}

	resetVideo() {
		if (this.bunnyPlayer) {
			this.bunnyPlayer.reset()
		}
	}

	playVideo() {
		if (this.bunnyPlayer) {
			this.bunnyPlayer.play()
		}
	}
}
